import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { DataGridColumn } from '../DataGrid.types';

export interface UseColumnReorderOptions<T extends Record<string, unknown>> {
  columns: DataGridColumn<T>[];
  onColumnReorder?: (newOrder: string[]) => void;
}

export interface UseColumnReorderReturn<T extends Record<string, unknown>> {
  orderedColumns: DataGridColumn<T>[];
  columnOrder: string[];
  draggedColumnId: string | null;
  dropIndicatorIndex: number | null;
  isDragging: boolean;
  getDragHandlers: (columnId: string) => {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragEnd: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  getColumnStyle: (columnId: string) => React.CSSProperties;
  resetColumnOrder: () => void;
}

export function useColumnReorder<T extends Record<string, unknown>>({
  columns,
  onColumnReorder
}: UseColumnReorderOptions<T>): UseColumnReorderReturn<T> {
  const [columnOrder, setColumnOrder] = useState<string[]>(() => columns.map(c => c.id));
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [dropIndicatorIndex, setDropIndicatorIndex] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const columnWidthsRef = useRef<Map<string, number>>(new Map());
  const columnBoundsRef = useRef<{ id: string; left: number; right: number; center: number }[]>([]);

  const dragStateRef = useRef<{
    draggedColumnId: string | null;
    draggedIndex: number;
    dropIndicatorIndex: number | null;
    columnOrder: string[];
    lastIndicatorIndex: number | null;
    lastUpdateTime: number;
  }>({
    draggedColumnId: null,
    draggedIndex: -1,
    dropIndicatorIndex: null,
    columnOrder: [],
    lastIndicatorIndex: null,
    lastUpdateTime: 0
  });

  useEffect(() => {
    dragStateRef.current.draggedColumnId = draggedColumnId;
    dragStateRef.current.dropIndicatorIndex = dropIndicatorIndex;
  }, [draggedColumnId, dropIndicatorIndex]);

  useEffect(() => {
    dragStateRef.current.columnOrder = columnOrder;
  }, [columnOrder]);

  useEffect(() => {
    const currentIds = new Set(columnOrder);
    const newIds = columns.map(c => c.id);
    if (newIds.length !== columnOrder.length || newIds.some(id => !currentIds.has(id))) {
      setColumnOrder(newIds);
    }
  }, [columns, columnOrder]);

  const orderedColumns = useMemo(() => {
    const columnMap = new Map(columns.map(c => [c.id, c]));
    return columnOrder
      .map(id => columnMap.get(id))
      .filter((c): c is DataGridColumn<T> => c !== undefined);
  }, [columns, columnOrder]);

  // AG Grid approach: Don't transform columns during drag to prevent flickering
  // Only show drop indicator and apply style to dragged column
  // Columns reorder on drop, not during drag
  const getColumnStyle = useCallback((columnId: string): React.CSSProperties => {
    const isThisDragged = columnId === draggedColumnId;

    if (isAnimating) {
      return {
        opacity: 1,
        transition: 'none'
      };
    }

    // Only style the dragged column - make it semi-transparent
    if (isThisDragged) {
      return {
        opacity: 0.5,
        background: 'var(--color-primary-50)',
        boxShadow: 'inset 0 0 0 2px var(--color-primary-300)',
        transition: 'opacity 0.15s ease, background 0.15s ease'
      };
    }

    // Other columns stay in place - no transform!
    // This prevents the feedback loop that causes flickering
    return {
      opacity: 1,
      transition: 'opacity 0.15s ease'
    };
  }, [draggedColumnId, isAnimating]);

  // Calculate drop index based on mouse X position
  const calculateDropIndex = useCallback((mouseX: number): number | null => {
    const bounds = columnBoundsRef.current;
    const dragIdx = dragStateRef.current.draggedIndex;
    
    if (bounds.length === 0 || dragIdx === -1) return null;

    // Find the closest column edge
    let closestEdge = 0;
    let minDistance = Infinity;

    for (let i = 0; i <= bounds.length; i++) {
      let edgeX: number;
      if (i === 0) {
        edgeX = bounds[0].left;
      } else if (i === bounds.length) {
        edgeX = bounds[bounds.length - 1].right;
      } else {
        // Use the midpoint between columns as the edge
        edgeX = (bounds[i - 1].right + bounds[i].left) / 2;
      }

      const distance = Math.abs(mouseX - edgeX);
      if (distance < minDistance) {
        minDistance = distance;
        closestEdge = i;
      }
    }

    // Don't show indicator if dropping at same position
    if (closestEdge === dragIdx || closestEdge === dragIdx + 1) {
      return null;
    }

    return closestEdge;
  }, []);

  const getDragHandlers = useCallback((columnId: string) => {
    const onDragStart = (e: React.DragEvent) => {
      const element = e.currentTarget as HTMLElement;
      const draggedIndex = dragStateRef.current.columnOrder.indexOf(columnId);
      dragStateRef.current.draggedIndex = draggedIndex;
      dragStateRef.current.draggedColumnId = columnId;
      dragStateRef.current.dropIndicatorIndex = null;
      dragStateRef.current.lastIndicatorIndex = null;
      dragStateRef.current.lastUpdateTime = 0;

      // Calculate and store all column bounds
      const row = element.parentElement;
      if (row) {
        const headers = row.querySelectorAll('th');
        const bounds: { id: string; left: number; right: number; center: number }[] = [];
        let colIndex = 0;
        
        headers.forEach((th) => {
          if (th.querySelector('input[type="checkbox"]')) {
            return;
          }
          const id = dragStateRef.current.columnOrder[colIndex];
          if (id) {
            const rect = th.getBoundingClientRect();
            columnWidthsRef.current.set(id, th.offsetWidth);
            bounds.push({
              id,
              left: rect.left,
              right: rect.right,
              center: rect.left + rect.width / 2
            });
          }
          colIndex++;
        });
        
        columnBoundsRef.current = bounds;
      }

      setDraggedColumnId(columnId);
      setDropIndicatorIndex(null);
      setIsAnimating(false);

      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', columnId);

      const clone = element.cloneNode(true) as HTMLElement;
      clone.style.width = element.offsetWidth + 'px';
      clone.style.height = element.offsetHeight + 'px';
      clone.style.background = 'var(--color-bg-card)';
      clone.style.border = '2px solid var(--color-primary)';
      clone.style.borderRadius = '4px';
      clone.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
      clone.style.opacity = '0.95';
      clone.style.position = 'absolute';
      clone.style.top = '-1000px';
      clone.style.left = '-1000px';
      clone.style.zIndex = '9999';
      clone.style.pointerEvents = 'none';
      document.body.appendChild(clone);

      e.dataTransfer.setDragImage(clone, element.offsetWidth / 2, element.offsetHeight / 2);

      requestAnimationFrame(() => {
        if (clone.parentNode) {
          document.body.removeChild(clone);
        }
      });
    };

    const onDragEnd = () => {
      const { draggedIndex, dropIndicatorIndex: finalDropIndex, columnOrder: currentOrder } = dragStateRef.current;

      if (finalDropIndex !== null && draggedIndex !== -1) {
        let targetIndex = finalDropIndex;
        if (draggedIndex < finalDropIndex) {
          targetIndex = finalDropIndex - 1;
        }
        if (targetIndex !== draggedIndex && targetIndex >= 0 && targetIndex < currentOrder.length) {
          setIsAnimating(true);
          
          requestAnimationFrame(() => {
            const newOrder = [...currentOrder];
            const [removed] = newOrder.splice(draggedIndex, 1);
            newOrder.splice(targetIndex, 0, removed);
            setColumnOrder(newOrder);
            
            if (onColumnReorder) {
              onColumnReorder(newOrder);
            }
            
            setTimeout(() => {
              setIsAnimating(false);
            }, 50);
          });
        }
      }

      dragStateRef.current.draggedColumnId = null;
      dragStateRef.current.draggedIndex = -1;
      dragStateRef.current.dropIndicatorIndex = null;
      dragStateRef.current.lastIndicatorIndex = null;
      columnBoundsRef.current = [];
      setDraggedColumnId(null);
      setDropIndicatorIndex(null);
    };

    const onDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      if (!dragStateRef.current.draggedColumnId) return;

      const now = Date.now();
      // Throttle updates to prevent flickering (min 50ms between updates)
      if (now - dragStateRef.current.lastUpdateTime < 50) {
        return;
      }

      const newDropIndex = calculateDropIndex(e.clientX);
      
      // Only update if actually changed
      if (newDropIndex !== dragStateRef.current.lastIndicatorIndex) {
        dragStateRef.current.lastIndicatorIndex = newDropIndex;
        dragStateRef.current.dropIndicatorIndex = newDropIndex;
        dragStateRef.current.lastUpdateTime = now;
        setDropIndicatorIndex(newDropIndex);
      }
    };

    const onDragLeave = (e: React.DragEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      if (!relatedTarget || !e.currentTarget.parentElement?.contains(relatedTarget)) {
        dragStateRef.current.dropIndicatorIndex = null;
        dragStateRef.current.lastIndicatorIndex = null;
        setDropIndicatorIndex(null);
      }
    };

    const onDrop = (e: React.DragEvent) => {
      e.preventDefault();
    };

    return {
      draggable: true,
      onDragStart,
      onDragEnd,
      onDragOver,
      onDragLeave,
      onDrop
    };
  }, [onColumnReorder, calculateDropIndex]);

  const resetColumnOrder = useCallback(() => {
    setColumnOrder(columns.map(c => c.id));
  }, [columns]);

  return {
    orderedColumns,
    columnOrder,
    draggedColumnId,
    dropIndicatorIndex,
    isDragging: draggedColumnId !== null,
    getDragHandlers,
    getColumnStyle,
    resetColumnOrder
  };
}
